// import { useMemo } from 'react';
// import { decodeJwt } from '@src/utils/decode-jwt';
// //@ts-ignore
// import Cookies from 'js-cookie';
// import { config } from '@src/config';

// const useTokenExpiration = () => {
//     const isTokenExpired = useMemo(() => {
//         const token = Cookies.get(config.keyNameCookie);

//         if (!token) {
//             return true;
//         }

//         try {
//             const decodedToken = decodeJwt(token);
//             const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds

//             if (!expirationTime) {
//                 return true;
//             }

//             return Date.now() > expirationTime;
//         } catch (error) {
//             console.error('Error decoding token:', error);
//             return true;
//         }
//     }, []);

//     return isTokenExpired;
// };

// export default useTokenExpiration;

import { useState, useEffect, useCallback } from 'react';
import { decodeJwt } from '@src/utils/decode-jwt';

interface TokenExpirationOptions {
    cookieName?: string;
    checkInterval?: number;
    bufferTime?: number; // Time in seconds before expiration to consider token expired
}

interface TokenInfo {
    isExpired: boolean;
    expirationTime: number | null;
    timeUntilExpiration: number | null;
}

// Utility function to get cookie value
const getCookieValue = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
};

// Utility function to check if token is expired
const checkTokenExpiration = (token: string, bufferTime: number = 60): TokenInfo => {
    try {
        const payload = decodeJwt(token);

        if (!payload.exp || typeof payload.exp !== 'number') {
            return {
                isExpired: true,
                expirationTime: null,
                timeUntilExpiration: null
            };
        }

        const currentTime = Math.floor(Date.now() / 1000);
        const expirationTime = payload.exp;
        const timeUntilExpiration = expirationTime - currentTime;
        const isExpired = timeUntilExpiration <= bufferTime;

        return {
            isExpired,
            expirationTime,
            timeUntilExpiration: timeUntilExpiration > 0 ? timeUntilExpiration : 0
        };
    } catch (error) {
        console.warn('Failed to decode token:', error);
        return {
            isExpired: true,
            expirationTime: null,
            timeUntilExpiration: null
        };
    }
};

export const useTokenExpiration = ({
    cookieName = 'zero-carbon-auth.session-token',
    checkInterval = 60000, // Check every minute
    bufferTime = 60 // Consider expired 60 seconds before actual expiration
}: TokenExpirationOptions = {}): boolean => {
    const [isExpired, setIsExpired] = useState<boolean>(true);

    // Memoize the token checking logic
    const checkExpiration = useCallback(() => {
        const token = getCookieValue(cookieName);

        if (!token) {
            setIsExpired(true);
            return;
        }

        const tokenInfo = checkTokenExpiration(token, bufferTime);
        setIsExpired(tokenInfo.isExpired);

        // Log warning if token expires soon
        if (!tokenInfo.isExpired && tokenInfo.timeUntilExpiration && tokenInfo.timeUntilExpiration < 300) {
            console.warn(`Token expires in ${tokenInfo.timeUntilExpiration} seconds`);
        }
    }, [cookieName, bufferTime]);

    // Effect for initial check and periodic checking
    useEffect(() => {
        // Initial check
        checkExpiration();

        // Set up interval for periodic checks
        const interval = setInterval(checkExpiration, checkInterval);

        // Listen for visibility change to check when tab becomes active
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkExpiration();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkExpiration, checkInterval]);

    return isExpired;
};

// Advanced version that returns more detailed token information
export const useTokenExpirationInfo = ({
    cookieName = 'zero-carbon-auth.session-token',
    checkInterval = 60000,
    bufferTime = 60
}: TokenExpirationOptions = {}): TokenInfo & { refreshToken: () => void } => {
    const [tokenInfo, setTokenInfo] = useState<TokenInfo>({
        isExpired: true,
        expirationTime: null,
        timeUntilExpiration: null
    });

    const checkExpiration = useCallback(() => {
        const token = getCookieValue(cookieName);

        if (!token) {
            setTokenInfo({
                isExpired: true,
                expirationTime: null,
                timeUntilExpiration: null
            });
            return;
        }

        const newTokenInfo = checkTokenExpiration(token, bufferTime);
        setTokenInfo(newTokenInfo);
    }, [cookieName, bufferTime]);

    useEffect(() => {
        checkExpiration();
        const interval = setInterval(checkExpiration, checkInterval);

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkExpiration();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkExpiration, checkInterval]);

    return {
        ...tokenInfo,
        refreshToken: checkExpiration
    };
};